import {
  AgentReferral,
  AgentResources,
  InfoCard
} from "./chunk-KDZFAM5Y.js";
import {
  Actions,
  DateQuestion,
  Questionnaire,
  SecurityLocks,
  SubmitAction
} from "./chunk-6GZTEA33.js";
import {
  AgentActions,
  AgentInformation,
  CoverToggle,
  CoverTogglesContainer,
  Headline,
  QuoteCard,
  QuoteCards
} from "./chunk-P47KBN3Y.js";
import {
  CoverageSummary,
  NextCoverage,
  Referral,
  TrustSection
} from "./chunk-OOWLXEPD.js";
import {
  QuoteHelp,
  formatAmount
} from "./chunk-V5XP6XCT.js";
import "./chunk-HA6TMEBG.js";
import "./chunk-6AC2CODT.js";
import "./chunk-6YYILA2X.js";
import {
  ErrorBoundary,
  Footer,
  Header,
  InsuranceBenefits,
  LOGO_LINK,
  OPENING_HOURS_LABEL,
  OPENING_HOURS_UK,
  OPENING_HOURS_US,
  Page,
  PageProviders,
  PreFooter,
  ProgressSteps,
  ProgressStepsItem
} from "./chunk-KA7SO5SJ.js";

// src/components/shared/DialogueModal/DialogueModal.tsx
import { Button, Drawer, Modal, TextOrHTML } from "@simplybusiness/mobius";
import { useEffect as useEffect2, useState } from "react";

// src/components/shared/PersistentSummary/helpers/PersistentPaymentSummaryHelper.tsx
import { useEffect } from "react";

// src/components/shared/PersistentSummary/UtilsHelper.tsx
var currencyBasedLocale = { USD: "en-US", GBP: "en-GB" };
function currencyFormatter(currency) {
  return new Intl.NumberFormat(
    currencyBasedLocale[currency.toUpperCase()],
    {
      style: "currency",
      currency
    }
  );
}
function fetchRequest(url, payload) {
  const journeyTokenElement = document.querySelector("[name='journey_token']");
  const authenticityTokenElement = document.querySelector(
    "[name='authenticity_token']"
  );
  if (!journeyTokenElement || !authenticityTokenElement) {
    return Promise.reject(new Error("Tokens not found"));
  }
  const journeyToken = journeyTokenElement.value;
  const authenticityToken = authenticityTokenElement.value;
  return fetch(`${url}&journey_token=${journeyToken}`, {
    method: "POST",
    body: payload,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken
    }
  }).then((result) => {
    return result.json();
  }).then((json) => {
    return Promise.resolve(json);
  }).catch((error) => {
    return Promise.reject(new Error(error));
  });
}

// src/components/shared/PersistentSummary/helpers/PersistentPaymentSummaryHelper.tsx
var notUndefined = (value) => typeof value !== "undefined";
var currencyBasedLocale2 = {
  USD: "en-US",
  GBP: "en-GB"
};
var paymentOptions = { ANNUAL: "annual", MONTHLY: "monthly" };
var productInfoTexts = (productsInfo, key) => {
  return productsInfo.map((productInfo) => productInfo[key]).filter(notUndefined).filter((value) => typeof value === "string");
};
var upfrontPaymentAmount = (productInfo) => {
  if (productInfo.selectedPaymentOption === paymentOptions.ANNUAL) {
    return productInfo.availablePaymentOptions.annual.amount;
  }
  return productInfo.availablePaymentOptions.monthly.downpaymentAmount;
};
var monthlyProductsInfo = (productsInfo) => {
  return productsInfo.reduce((accumulator, productInfo) => {
    if (productInfo.selectedPaymentOption === paymentOptions.MONTHLY) {
      return accumulator.concat(productInfo);
    }
    return accumulator;
  }, []);
};
var upfrontPayments = (productsInfo, currency) => {
  return productsInfo.map((productInfo) => ({
    labels: productInfo.coverNames,
    value: currencyFormatter(currency).format(
      upfrontPaymentAmount(productInfo)
    )
  }));
};
var upfrontPaymentsTotal = (productsInfo, currency) => {
  const initialValue = 0;
  const total = productsInfo.reduce((accumulator, productInfo) => {
    return accumulator + upfrontPaymentAmount(productInfo);
  }, initialValue);
  return currencyFormatter(currency).format(total);
};
var monthlyPayments = (productsInfo, currency, hasMonthlyFees = false) => {
  const monthlyProductsData = monthlyProductsInfo(productsInfo);
  if (!monthlyProductsData.length) return [];
  const flattenInstallmentsSchedule = monthlyProductsData.flatMap(
    (productInfo) => {
      return productInfo.availablePaymentOptions.monthly.installmentsSchedule;
    }
  );
  const installmentsSchedule = Object.values(
    flattenInstallmentsSchedule.reduce(
      (acc, { month, year, amount }) => {
        const formattedMonth = `${year}-${month}`;
        acc[formattedMonth] = {
          month: formattedMonth,
          amount: (acc[formattedMonth] ? acc[formattedMonth].amount : 0) + amount
        };
        return acc;
      },
      {}
    )
  ).sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  const groupedAmounts = installmentsSchedule.reduce(
    (acc, schedule) => {
      if (acc.last && acc.last !== schedule.amount) {
        acc.result.push([]);
      }
      acc.result[acc.result.length - 1].push(schedule.amount);
      return { ...acc, last: schedule.amount };
    },
    { result: [[]], last: null }
  ).result;
  return groupedAmounts.map((group, index) => {
    const prefix = index === 0 ? "" : "Then ";
    const labels = hasMonthlyFees ? productsInfo[0].coverNames : [`${prefix}${group.length} monthly payments of`];
    return {
      labels,
      value: currencyFormatter(currency).format(group[0])
    };
  });
};
var firstMonthlyPaymentDate = (productsInfo, currency) => {
  const monthlyPaymentDates = monthlyProductsInfo(productsInfo).map(
    (productInfo) => {
      return new Date(
        productInfo.availablePaymentOptions.monthly.firstInstallmentDate
      );
    }
  );
  return new Date(
    Math.min(...monthlyPaymentDates.map((date) => date.getTime()))
  ).toLocaleDateString(currencyBasedLocale2[currency.toUpperCase()], {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  });
};
var monthlyPaymentSummary = (productsInfo, currency) => {
  const monthlyProducts = monthlyProductsInfo(productsInfo);
  const payments = monthlyProducts.map((productInfo) => ({
    installmentCount: productInfo.availablePaymentOptions?.monthly?.installmentsSchedule?.length ?? 0,
    monthlyAmount: Number(
      productInfo.availablePaymentOptions?.monthly?.installmentAmount ?? 0
    )
  }));
  if (!payments.length) return [];
  const uniqueInstallmentCounts = [
    ...new Set(payments.map((payment) => payment.installmentCount))
  ].sort((a, b) => a - b);
  const allProductsHaveSameInstallmentCount = uniqueInstallmentCounts.length === 1;
  const createPaymentTier = (paymentCount, totalAmount, isFirstTier) => {
    const pluralSuffix = paymentCount > 1 ? "s" : "";
    const prefix = isFirstTier ? "" : "Then ";
    return {
      label: `${prefix}${paymentCount} monthly payment${pluralSuffix} of`,
      value: currencyFormatter(currency).format(totalAmount)
    };
  };
  if (allProductsHaveSameInstallmentCount) {
    const installmentCount = uniqueInstallmentCounts[0];
    const totalMonthlyAmount = payments.reduce(
      (sum, payment) => sum + payment.monthlyAmount,
      0
    );
    return [createPaymentTier(installmentCount, totalMonthlyAmount, true)];
  } else {
    const tiers = [];
    let previousInstallmentCount = 0;
    for (const currentInstallmentCount of uniqueInstallmentCounts) {
      const installmentCountForTier = currentInstallmentCount - previousInstallmentCount;
      const monthlyPaymentAmountForTier = payments.reduce((sum, payment) => {
        return payment.installmentCount >= currentInstallmentCount ? sum + payment.monthlyAmount : sum;
      }, 0);
      const isFirstTier = previousInstallmentCount === 0;
      tiers.push(
        createPaymentTier(
          installmentCountForTier,
          monthlyPaymentAmountForTier,
          isFirstTier
        )
      );
      previousInstallmentCount = currentInstallmentCount;
    }
    return tiers;
  }
};
var selectProductsMarkedForPurchase = (products) => products.filter((product) => product.markedForPurchase);
var useTriggerEventTracker = (eventName, ...args) => {
  useEffect(() => {
    if (eventName && window) {
      window.EventTracker?.trigger(eventName, ...args);
    }
  }, [eventName, args]);
};

// src/components/shared/DialogueModal/ctaAction.tsx
var ctaAction = (fieldName, setEventDetails) => {
  const checkboxes = document.querySelectorAll(
    ".s-step-recommendations input[type=checkbox]"
  );
  checkboxes.forEach((box) => {
    if (box.name === fieldName) {
      box.click();
      if (box.checked === true) {
        setEventDetails({
          eventName: "coverage_selection_recommendations_coverage_added",
          args: [fieldName]
        });
      } else {
        setEventDetails({
          eventName: "coverage_selection_recommendations_coverage_removed",
          args: [fieldName]
        });
      }
    }
  });
};

// src/components/shared/DialogueModal/DialogueModal.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var productNameMap = {
  "General liability": "General Liability",
  "Professional liability": "Professional Liability / Errors & Omissions",
  Cyber: "Cyber",
  "Inland marine": "Business Equipment and Small Tools",
  "Business Owners Policy": "Business Owners Policy"
};
var DialogueModal = ({
  name,
  fieldName,
  productInfo,
  text: text3 = "Learn more",
  isDrawer = false
}) => {
  const [open, setOpen] = useState(false);
  const [buttonLabel, setButtonLabel] = useState("Add this coverage");
  const [buttonStyle, setButtonStyle] = useState(false);
  const [eventDetails, setEventDetails] = useState({ eventName: null, args: [] });
  useTriggerEventTracker(eventDetails.eventName, ...eventDetails.args);
  const handleCtaClick = () => {
    ctaAction(fieldName, setEventDetails);
    setButtonLabel(
      buttonLabel === "Add this coverage" ? "Remove this coverage" : "Add this coverage"
    );
    setButtonStyle(!buttonStyle);
    setOpen(false);
  };
  const handleDialogueClick = () => {
    setOpen(true);
    if (fieldName) {
      setEventDetails({
        eventName: "coverage_selection_learn_more_modal_opened",
        args: [fieldName.replace(/_coverage$/, "")]
      });
    }
  };
  useEffect2(() => {
    const checkboxes = document.querySelectorAll(
      ".s-step-recommendations input[type=checkbox]"
    );
    const toggleLogic = (box) => {
      if (box && box.checked === false) {
        setButtonLabel("Add this coverage");
        setButtonStyle(false);
      } else {
        setButtonLabel("Remove this coverage");
        setButtonStyle(true);
      }
    };
    checkboxes.forEach((box) => {
      box.addEventListener("change", () => {
        toggleLogic(box);
      });
      if (box.name === fieldName) {
        toggleLogic(box);
      }
    });
  }, [buttonLabel, fieldName]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        className: "mobius-dialogue-modal__styled-button",
        size: "sm",
        onClick: () => {
          handleDialogueClick();
        },
        children: text3
      }
    ),
    !isDrawer && /* @__PURE__ */ jsxs(Modal, { isOpen: open, onClose: () => setOpen(false), animation: "slideUp", children: [
      /* @__PURE__ */ jsx(Modal.Header, { children: productNameMap[name] || name }),
      /* @__PURE__ */ jsx(Modal.Content, { children: /* @__PURE__ */ jsx("div", { className: "mobius-dialogue-modal__description mobius-dialogue-modal__description_non_drawer", children: /* @__PURE__ */ jsx(TextOrHTML, { text: productInfo, htmlElementType: "div" }) }) })
    ] }),
    isDrawer && /* @__PURE__ */ jsxs(
      Drawer,
      {
        isOpen: open,
        onClose: () => setOpen(false),
        direction: "right",
        closeLabel: "Close",
        children: [
          /* @__PURE__ */ jsx(Drawer.Header, { children: /* @__PURE__ */ jsx("div", { className: "mobius-dialogue-modal__header", children: productNameMap[name] || name }) }),
          /* @__PURE__ */ jsx(Drawer.Content, { children: /* @__PURE__ */ jsxs("div", { className: "mobius-dialogue-modal__description mobius-dialogue-modal__description_drawer", children: [
            /* @__PURE__ */ jsx("div", { className: "mobius-dialogue-modal__styled-drawer-content", children: /* @__PURE__ */ jsx(TextOrHTML, { text: productInfo, htmlElementType: "div" }) }),
            /* @__PURE__ */ jsx("div", { className: "mobius-dialogue-modal__styled-cta-wrapper", children: /* @__PURE__ */ jsx(
              Button,
              {
                className: `mobius-dialogue-modal__styled-cta ${buttonStyle ? "--is-ghost" : ""}`,
                onClick: () => {
                  handleCtaClick();
                },
                children: buttonLabel
              }
            ) })
          ] }) })
        ]
      }
    )
  ] });
};

// src/components/shared/PersistentSummary/PersistentCartPaymentOptions/PersistentCartPaymentOptions.tsx
import { useState as useState6, useEffect as useEffect3, useMemo } from "react";
import { SegmentGroup as SegmentGroup2 } from "@simplybusiness/mobius";

// src/components/shared/PersistentSummary/PaymentSummary/index.tsx
import { useContext as useContext2 } from "react";
import { SegmentGroup, Segment as Segment2, Text as Text2, Flex } from "@simplybusiness/mobius";

// src/components/shared/PersistentSummary/CombinedPaymentContext.tsx
import { createContext } from "react";
var CombinedPaymentContext = createContext(false);

// src/components/shared/PersistentSummary/PaymentSummary/PaymentItemsSection.tsx
import { Divider, Segment, Text } from "@simplybusiness/mobius";
import React, { useContext } from "react";
import { Fragment as Fragment2, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var PaymentItemsSection = ({
  headerText,
  headerItem,
  items,
  summaryItems,
  isExternalCarrier = false
}) => {
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);
  return /* @__PURE__ */ jsxs2(Segment, { "data-testid": "payment-items-section", children: [
    /* @__PURE__ */ jsx2("div", { className: "mobius-payment-summary__section-header-text", children: headerText }),
    /* @__PURE__ */ jsxs2("div", { className: "mobius-payment-summary__section-header", children: [
      headerItem && !isExternalCarrier && /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsx2(Text, { variant: "small", spacing: "tight", children: /* @__PURE__ */ jsx2("strong", { className: "mobius-payment-summary__header-label", children: headerItem.label }) }),
        /* @__PURE__ */ jsx2(
          Text,
          {
            variant: "small",
            spacing: "tight",
            className: "mobius-payment-summary__justified-text",
            children: /* @__PURE__ */ jsx2("strong", { className: "mobius-payment-summary__header-label", children: headerItem.value })
          }
        )
      ] }),
      items.map((item, index) => /* @__PURE__ */ jsxs2(React.Fragment, { children: [
        /* @__PURE__ */ jsx2("div", { className: "mobius-payment-summary__item-grid", children: item.labels.map((label) => /* @__PURE__ */ jsx2(Text, { variant: "small", spacing: "tight", children: label }, label)) }),
        /* @__PURE__ */ jsx2(
          Text,
          {
            variant: "small",
            spacing: "tight",
            className: "mobius-payment-summary__justified-text",
            children: item.value
          }
        )
      ] }, ["item", index].join("-")))
    ] }),
    summaryItems && summaryItems.length > 0 && /* @__PURE__ */ jsxs2(Fragment2, { children: [
      /* @__PURE__ */ jsx2(
        Divider,
        {
          className: isCombinedPaymentPage ? "combined-styles" : void 0
        }
      ),
      summaryItems.map((item, index) => /* @__PURE__ */ jsxs2(
        "div",
        {
          className: "mobius-payment-summary__summary-item",
          children: [
            /* @__PURE__ */ jsx2(
              Text,
              {
                variant: "small",
                spacing: "tight",
                className: isCombinedPaymentPage ? "mobius-payment-summary__combined-payment-page-text" : "mobius-payment-summary__default-payment-page-text",
                children: item.label
              }
            ),
            /* @__PURE__ */ jsx2(
              Text,
              {
                variant: "small",
                spacing: "tight",
                className: isCombinedPaymentPage ? "mobius-payment-summary__combined-payment-page-text mobius-payment-summary__text-align-right" : "mobius-payment-summary__default-payment-page-text mobius-payment-summary__text-align-right",
                children: item.value
              }
            )
          ]
        },
        `summary-item-${index}`
      ))
    ] })
  ] });
};

// src/components/shared/PersistentSummary/PaymentSummary/index.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var PaymentSummary = ({
  primaryTrade,
  businessName,
  upfrontPayments: upfrontPayments2,
  upfrontPaymentsTotal: upfrontPaymentsTotal2,
  monthlyPayments: monthlyPayments2,
  monthlyPaymentSummary: monthlyPaymentSummary2,
  firstMonthlyPaymentDate: firstMonthlyPaymentDate2,
  totalPaymentAmount,
  nonRefundableTexts,
  surchargeTexts,
  multiProductText,
  cardFeeDisclosureText,
  isExternalCarrier,
  hasMonthlyFees
}) => {
  const isCombinedPaymentPage = useContext2(CombinedPaymentContext);
  const areMonthlyPaymentsAvailable = monthlyPayments2.length > 0;
  const upfrontPaymentsSummaryItem = () => {
    if (areMonthlyPaymentsAvailable) {
      return [
        {
          label: "To pay today",
          value: upfrontPaymentsTotal2
        }
      ];
    }
    return void 0;
  };
  const monthlyPaymentsHeaderItem = {
    label: "First monthly payment taken",
    value: firstMonthlyPaymentDate2
  };
  return /* @__PURE__ */ jsxs3("div", { children: [
    /* @__PURE__ */ jsxs3(
      SegmentGroup,
      {
        "data-testid": "payment-summary",
        className: isCombinedPaymentPage ? "combined-styles" : void 0,
        children: [
          !isCombinedPaymentPage ? /* @__PURE__ */ jsx3(Segment2, { "data-testid": "business-information-section", children: /* @__PURE__ */ jsxs3("div", { className: "mobius-payment-summary__item-grid", children: [
            /* @__PURE__ */ jsx3(Text2, { variant: "h4", children: `${primaryTrade} insurance` }),
            /* @__PURE__ */ jsx3(Text2, { variant: "small", spacing: "tight", children: /* @__PURE__ */ jsx3("strong", { children: businessName }) })
          ] }) }) : null,
          /* @__PURE__ */ jsx3(
            PaymentItemsSection,
            {
              "data-testid": "today-payment-section",
              headerText: "Today's payment",
              items: upfrontPayments2,
              summaryItems: upfrontPaymentsSummaryItem()
            }
          ),
          areMonthlyPaymentsAvailable && /* @__PURE__ */ jsx3(
            PaymentItemsSection,
            {
              "data-testid": "monthly-payment-section",
              headerText: "Monthly payments",
              headerItem: monthlyPaymentsHeaderItem,
              items: monthlyPayments2,
              summaryItems: hasMonthlyFees ? monthlyPaymentSummary2 : void 0,
              isExternalCarrier
            }
          ),
          /* @__PURE__ */ jsx3(Segment2, { colour: "subtle", children: !isCombinedPaymentPage ? /* @__PURE__ */ jsxs3(
            Flex,
            {
              className: "mobius-payment-summary__styled-flex",
              "data-testid": "total-payment-amount",
              children: [
                /* @__PURE__ */ jsx3(Text2, { children: "Total (inc. tax + fees)" }),
                /* @__PURE__ */ jsx3(Text2, { children: totalPaymentAmount })
              ]
            }
          ) : /* @__PURE__ */ jsxs3(
            Flex,
            {
              className: "mobius-payment-summary__styled-flex",
              "data-testid": "total-payment-amount",
              children: [
                /* @__PURE__ */ jsxs3(Text2, { children: [
                  /* @__PURE__ */ jsx3("strong", { children: "Total" }),
                  " (inc. tax + fees)"
                ] }),
                /* @__PURE__ */ jsx3(Text2, { children: /* @__PURE__ */ jsx3("strong", { children: totalPaymentAmount }) })
              ]
            }
          ) })
        ]
      }
    ),
    /* @__PURE__ */ jsx3(Text2, { variant: "legal", children: multiProductText }),
    nonRefundableTexts.map((text3, index) => /* @__PURE__ */ jsx3(Text2, { variant: "legal", children: text3 }, ["non-refundable-text", index].join("-"))),
    surchargeTexts.map((text3, index) => /* @__PURE__ */ jsx3(Text2, { variant: "legal", children: text3 }, ["surcharge-text", index].join("-"))),
    /* @__PURE__ */ jsx3(Text2, { variant: "legal", children: cardFeeDisclosureText })
  ] });
};

// src/components/shared/PersistentSummary/PaymentOptions/index.tsx
import { partyHorn } from "@simplybusiness/icons";
import {
  Alert,
  Button as Button3,
  Icon,
  RadioGroup,
  Segment as Segment3,
  Text as Text4
} from "@simplybusiness/mobius";
import { useContext as useContext4, useState as useState3 } from "react";

// src/components/shared/PersistentSummary/DialogueModal.tsx
import { Button as Button2, Modal as Modal2 } from "@simplybusiness/mobius";
import { useState as useState2 } from "react";
import { Fragment as Fragment3, jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var DialogueModal2 = ({
  redirectFunction,
  headerText,
  description,
  actionButtonText,
  cancelButtonText,
  button
}) => {
  const [openModal, setOpenModal] = useState2(false);
  const [loading, setIsLoading] = useState2(false);
  const [eventName, setEventName] = useState2(null);
  useTriggerEventTracker(eventName);
  const handleOpenModal = (ev) => {
    ev.preventDefault();
    setOpenModal(true);
  };
  const handleCancelButtonClick = (ev) => {
    ev.preventDefault();
    setOpenModal(false);
    setEventName("cancel_return_to_checkout_button_clicked");
  };
  const handleActionButtonClick = () => {
    setIsLoading(true);
    setEventName("go_back_to_quote_selection_clicked");
    redirectFunction();
  };
  return /* @__PURE__ */ jsxs4(Fragment3, { children: [
    /* @__PURE__ */ jsx4(Button2, { size: "sm", ...button?.props || {}, onClick: handleOpenModal }),
    /* @__PURE__ */ jsxs4(Modal2, { isOpen: openModal, onClose: () => setOpenModal(loading), children: [
      /* @__PURE__ */ jsx4(Modal2.Header, { children: headerText }),
      /* @__PURE__ */ jsxs4(Modal2.Content, { children: [
        description,
        /* @__PURE__ */ jsxs4("div", { className: "mobius-payment-summary__button-div", children: [
          /* @__PURE__ */ jsx4(
            Button2,
            {
              variant: "secondary",
              size: "sm",
              isLoading: loading,
              isDisabled: loading,
              onPress: handleActionButtonClick,
              children: actionButtonText
            }
          ),
          /* @__PURE__ */ jsx4(
            Button2,
            {
              variant: "ghost",
              size: "sm",
              isDisabled: loading,
              onClick: handleCancelButtonClick,
              children: cancelButtonText
            }
          )
        ] })
      ] })
    ] })
  ] });
};

// src/components/shared/PersistentSummary/PaymentOptions/PaymentOption.tsx
import { useContext as useContext3 } from "react";
import { Divider as Divider2, Flex as Flex2, Radio, Text as Text3 } from "@simplybusiness/mobius";
import { Fragment as Fragment4, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var PaymentOption = ({
  label,
  value,
  toPayToday,
  installment,
  currency,
  hasMonthlyFees,
  ...otherProps
}) => {
  const isCombinedPaymentPage = useContext3(CombinedPaymentContext);
  const formatter = currencyFormatter(currency);
  if (toPayToday == null) return null;
  return /* @__PURE__ */ jsx5(
    Radio,
    {
      className: "sb-brand",
      "data-testid": value,
      value,
      label,
      ...otherProps,
      children: /* @__PURE__ */ jsxs5(
        Flex2,
        {
          className: `${isCombinedPaymentPage ? "combined-styles" : void 0} mobius-payment-summary__styled-wrapper`,
          children: [
            /* @__PURE__ */ jsxs5(Flex2, { className: "mobius-payment-summary__styled-flex", children: [
              /* @__PURE__ */ jsx5(Text3, { variant: "small", className: "today-payment", children: "To pay today" }),
              /* @__PURE__ */ jsx5(Text3, { variant: "small", className: "today-payment", children: formatter.format(toPayToday) })
            ] }),
            installment && Object.keys(installment).length > 0 && /* @__PURE__ */ jsxs5(Fragment4, { children: [
              /* @__PURE__ */ jsxs5(Flex2, { className: "mobius-payment-summary__styled-flex", children: [
                /* @__PURE__ */ jsxs5(Text3, { variant: "small", className: "monthly-payment", children: [
                  installment.count,
                  " monthly payments of"
                ] }),
                /* @__PURE__ */ jsx5(Text3, { variant: "small", className: "monthly-payment", children: formatter.format(installment.amount) })
              ] }),
              /* @__PURE__ */ jsx5(Divider2, { className: "mobius-payment-summary__styled-divider" }),
              /* @__PURE__ */ jsxs5(Flex2, { className: "mobius-payment-summary__styled-flex", children: [
                /* @__PURE__ */ jsx5(Text3, { variant: "small", children: "Total" }),
                /* @__PURE__ */ jsx5(Text3, { variant: "small", className: "total-payment", children: hasMonthlyFees ? formatter.format(
                  installment.count * installment.amount + toPayToday
                ) : formatter.format(installment.total) })
              ] })
            ] })
          ]
        }
      )
    }
  );
};

// src/components/shared/PersistentSummary/PaymentOptions/index.tsx
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var ERRORS = {
  CART_ITEM_NOT_FOUND: "We encountered an issue updating your payment option. Please try selecting the same option again in a few moments.",
  SERVER_ERROR: "We encountered an unexpected issue. Please try again in a few moments or try refreshing the page."
};
var paymentOptions2 = { ANNUAL: "annual", MONTHLY: "monthly" };
var PaymentOptions = ({
  annual,
  monthly,
  selectedOption = "",
  currency,
  fireEvent,
  cartItemReference,
  markedForPurchase,
  onUpdateItem,
  promptInformation,
  enablePaymentOptionsServerUpdate,
  hasMonthlyFees,
  onPaymentOptionsClientUpdate
}) => {
  let installmentData = {};
  const [completeQuote, setIsCompleteQuote] = useState3(false);
  const [removeProduct, setIsRemovedProduct] = useState3(false);
  const [updatingPaymentOption, setUpdatingPaymentOption] = useState3(false);
  const [isError, setIsError] = useState3(false);
  const [errorMessage, setErrorMessage] = useState3("");
  const isCombinedPaymentPage = useContext4(CombinedPaymentContext);
  if (monthly && Object.keys(monthly).length > 0 && monthly.installmentsSchedule.length > 0) {
    const calculatedTotal = monthly.totalPayable ?? monthly.downpaymentAmount + monthly.installmentsSchedule.reduce(
      (sum, installment) => sum + installment.amount,
      0
    );
    installmentData = {
      downpayment: monthly.downpaymentAmount,
      count: monthly.installmentsSchedule.length,
      amount: monthly.installmentsSchedule[0].amount,
      total: calculatedTotal
    };
  }
  const calculateMonthlySavings = () => {
    if (installmentData.total) {
      return installmentData.total - annual.amount;
    }
    return 0;
  };
  const savingsAmount = calculateMonthlySavings();
  const promptButton = (className, loading, buttonText, onClickFn = () => {
  }) => /* @__PURE__ */ jsx6(
    Button3,
    {
      variant: "secondary",
      size: "sm",
      className,
      isLoading: loading,
      isDisabled: loading,
      onClick: () => onClickFn(),
      children: buttonText
    }
  );
  const removeFromCart = async (itemReference) => {
    const url = `?step_action=update_cart_item`;
    const payload = JSON.stringify({
      item_id: itemReference,
      action: "remove"
    });
    const response = await fetchRequest(url, payload);
    onUpdateItem(response, itemReference);
    if (response) setIsRemovedProduct(false);
  };
  const removeFromCartAndRedirect = async (itemReference, redirectUrl) => {
    const url = `?step_action=update_cart_item`;
    const payload = JSON.stringify({
      item_id: itemReference,
      action: "remove"
    });
    const response = await fetchRequest(url, payload);
    if (response) window.location.href = redirectUrl;
  };
  const handleServerPaymentOptionsChange = async (paymentOption) => {
    let response;
    try {
      setIsError(false);
      setUpdatingPaymentOption(true);
      fireEvent(cartItemReference, paymentOption);
      const url = `?step_action=update_cart_item`;
      const payload = JSON.stringify({
        action: "update_payment_option",
        item_id: cartItemReference,
        payment_option: paymentOption
      });
      response = await fetchRequest(url, payload);
      if (!response.success) {
        const message = response.message || "Error updating payment option";
        throw new Error(message);
      }
      onUpdateItem(response, cartItemReference);
    } catch {
      if (response && response.code === 404 && response.productsInformation && response.totalPaymentAmount) {
        setErrorMessage(ERRORS.CART_ITEM_NOT_FOUND);
        onUpdateItem(response, cartItemReference);
      } else {
        setErrorMessage(ERRORS.SERVER_ERROR);
      }
      setIsError(true);
    } finally {
      setUpdatingPaymentOption(false);
    }
  };
  const handlePaymentOptionsChange = async (paymentOption) => {
    if (enablePaymentOptionsServerUpdate) {
      await handleServerPaymentOptionsChange(paymentOption);
    } else {
      if (onPaymentOptionsClientUpdate) {
        onPaymentOptionsClientUpdate(cartItemReference, paymentOption);
      }
    }
  };
  return !promptInformation.promptCustomer ? /* @__PURE__ */ jsxs6(
    Segment3,
    {
      className: `subtle ${!markedForPurchase && "exclude-for-purchase"} ${isCombinedPaymentPage && "combined-styles"}`,
      children: [
        /* @__PURE__ */ jsx6(Text4, { elementType: "p", "data-testid": "payment-options-title", children: "Payment Options" }),
        isCombinedPaymentPage ? /* @__PURE__ */ jsxs6(
          RadioGroup,
          {
            className: "sb-brand",
            name: `payment_options[${cartItemReference}]`,
            "data-testid": "radio-group",
            defaultValue: selectedOption,
            "aria-label": "Payment options",
            onChange: (e) => {
              handlePaymentOptionsChange(e.target.value).catch((error) => {
                console.error("Error handling payment options change:", error);
              });
            },
            isDisabled: updatingPaymentOption,
            isInvalid: isError,
            errorMessage: isError ? errorMessage : void 0,
            children: [
              /* @__PURE__ */ jsx6(
                PaymentOption,
                {
                  label: "Pay monthly",
                  value: paymentOptions2.MONTHLY,
                  toPayToday: installmentData.downpayment || null,
                  currency,
                  installment: installmentData
                }
              ),
              /* @__PURE__ */ jsx6(
                PaymentOption,
                {
                  label: "Pay annually",
                  value: paymentOptions2.ANNUAL,
                  toPayToday: annual.amount,
                  currency
                }
              )
            ]
          }
        ) : /* @__PURE__ */ jsxs6(
          RadioGroup,
          {
            className: "sb-brand",
            name: `payment_options[${cartItemReference}]`,
            "data-testid": "radio-group",
            defaultValue: selectedOption,
            "aria-label": "Payment options",
            onChange: (e) => {
              handlePaymentOptionsChange(e.target.value).catch((error) => {
                console.error("Error handling payment options change:", error);
              });
            },
            isDisabled: updatingPaymentOption,
            isInvalid: isError,
            errorMessage: isError ? errorMessage : void 0,
            children: [
              /* @__PURE__ */ jsx6(
                PaymentOption,
                {
                  label: "Pay annually",
                  value: paymentOptions2.ANNUAL,
                  toPayToday: annual.amount,
                  currency
                }
              ),
              /* @__PURE__ */ jsx6(
                PaymentOption,
                {
                  label: "Pay monthly",
                  value: paymentOptions2.MONTHLY,
                  toPayToday: installmentData.downpayment || null,
                  currency,
                  installment: installmentData,
                  hasMonthlyFees
                }
              )
            ]
          }
        ),
        savingsAmount > 0 && /* @__PURE__ */ jsxs6(Text4, { variant: "small", className: "savings-message-success", children: [
          /* @__PURE__ */ jsx6(Icon, { icon: partyHorn }),
          "\xA0You save ",
          currencyFormatter(currency).format(savingsAmount),
          " by paying annually"
        ] })
      ]
    }
  ) : /* @__PURE__ */ jsx6(Segment3, { className: "non-eligible-item subtle", children: /* @__PURE__ */ jsxs6("div", { className: "mobius-payment-summary__styled-div", children: [
    /* @__PURE__ */ jsx6(Alert, { header: "Action required", variant: "error", children: promptInformation.message }),
    promptInformation.type === "completeQuote" ? promptButton(
      "incomplete-quote-actions",
      completeQuote,
      promptInformation.buttonText,
      () => {
        setIsCompleteQuote(true);
        void removeFromCartAndRedirect(
          cartItemReference,
          promptInformation.actionUrl
        );
      }
    ) : /* @__PURE__ */ jsx6(
      DialogueModal2,
      {
        cancelButtonText: "Cancel",
        headerText: "Resync your quote",
        actionButtonText: "Resync your quote",
        description: "In resyncing your coverage you will be taken back to your original quote selection.",
        redirectFunction: () => {
          setIsCompleteQuote(true);
          void removeFromCartAndRedirect(
            cartItemReference,
            promptInformation.actionUrl
          );
        },
        button: promptButton(
          "incomplete-quote-actions",
          completeQuote,
          promptInformation.buttonText
        )
      }
    ),
    /* @__PURE__ */ jsx6(
      Button3,
      {
        variant: "ghost",
        size: "sm",
        className: "incomplete-quote-actions",
        isLoading: removeProduct,
        isDisabled: removeProduct,
        onPress: () => {
          setIsRemovedProduct(true);
          void removeFromCart(cartItemReference);
        },
        children: "Remove this coverage"
      }
    )
  ] }) });
};

// src/components/shared/PersistentSummary/JourneyContinuationNotice/index.tsx
import { Segment as Segment4, Text as Text5 } from "@simplybusiness/mobius";
import { jsxs as jsxs7 } from "react/jsx-runtime";
var JourneyContinuationNotice = ({
  continuationProduct
}) => {
  return /* @__PURE__ */ jsxs7(Segment4, { children: [
    /* @__PURE__ */ jsxs7(Text5, { elementType: "h4", spacing: "loose", children: [
      "We did not forget about ",
      continuationProduct
    ] }),
    /* @__PURE__ */ jsxs7(Text5, { elementType: "span", children: [
      "We will get your ",
      continuationProduct,
      " quote right after you checkout."
    ] })
  ] });
};

// src/components/shared/PersistentSummary/PersistentCartPaymentOptions/PersistentCartPaymentOptions.tsx
import { useBreakpoint as useBreakpoint3 } from "@simplybusiness/mobius";

// src/components/shared/PersistentSummary/SummaryWrapper.tsx
import { useState as useState5, useContext as useContext6 } from "react";

// src/components/shared/PersistentSummary/ProductSummary/index.tsx
import { bin, circleTick } from "@simplybusiness/icons";
import {
  Button as Button4,
  Divider as Divider3,
  Flex as Flex3,
  Icon as Icon2,
  Text as Text6,
  useBreakpoint
} from "@simplybusiness/mobius";
import { Fragment as Fragment5, useContext as useContext5, useState as useState4 } from "react";
import { Fragment as Fragment6, jsx as jsx7, jsxs as jsxs8 } from "react/jsx-runtime";
function RenderItem(label, value, isPrimary) {
  return /* @__PURE__ */ jsxs8("div", { className: "mobius-payment-summary__styled-grid", children: [
    /* @__PURE__ */ jsx7(
      Text6,
      {
        elementType: "span",
        className: isPrimary ? "mobius-payment-summary__styled-grid-primary-text" : "mobius-payment-summary__styled-grid-secondary-text",
        children: label
      }
    ),
    /* @__PURE__ */ jsx7(
      Text6,
      {
        elementType: "span",
        className: "align-right " + (isPrimary ? "mobius-payment-summary__styled-grid-primary-text" : "mobius-payment-summary__styled-grid-secondary-text"),
        children: value
      }
    )
  ] });
}
function SummaryFragment(product, mobileView, policyStartDate, totalGrossPremium, hasSharedLimits, applicationDashboard, hasPrimaryCover = false) {
  const productCovers = Object.keys(product);
  const covers = ["Contractors' Equipment & Small Tools"];
  const breakpointSize = useBreakpoint([
    { size: "sm", value: 321 },
    { size: "md", value: 322 },
    { size: "lg", value: 641 }
  ]).breakpoint.size;
  const isMobile = breakpointSize == "md" || mobileView;
  const minMobileCovers = isMobile && productCovers.length > 1;
  const minDesktopCovers = !isMobile && productCovers.length > 0;
  const isCombinedPaymentPage = useContext5(CombinedPaymentContext);
  function showCover(cover) {
    const show = product[cover].displayPremium !== "";
    return show;
  }
  function showPolicyStartDate(idx) {
    const show = !applicationDashboard && policyStartDate;
    if (hasPrimaryCover) {
      return show && idx === 0 && hasSharedLimits;
    }
    return show;
  }
  function showPrimaryCoverDivider(idx) {
    return hasPrimaryCover && idx === 0 && hasSharedLimits;
  }
  function showSecondaryCoverDivider(idx) {
    return hasPrimaryCover && idx !== 0 && hasSharedLimits;
  }
  function showNonPrimaryCoverDivider(cover) {
    return !hasPrimaryCover && product[cover].displayPremium !== "" && policyStartDate;
  }
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: `mobius-payment-summary__summary-fragment-wrapper ${isCombinedPaymentPage ? "combined-styles" : void 0}`,
      children: [
        productCovers.map((cover, idx) => /* @__PURE__ */ jsxs8(Fragment5, { children: [
          showCover(cover) && /* @__PURE__ */ jsxs8(Fragment6, { children: [
            /* @__PURE__ */ jsx7(Flex3, { className: "mobius-payment-summary__cover", children: totalGrossPremium && (!hasSharedLimits || idx === 0 && hasSharedLimits) ? RenderItem(cover, totalGrossPremium, true) : RenderItem(cover, "", true) }),
            showPolicyStartDate(idx) && /* @__PURE__ */ jsxs8(Fragment6, { children: [
              /* @__PURE__ */ jsx7(
                Flex3,
                {
                  className: !isMobile ? "mobius-payment-summary__flex-margin-bottom" : "",
                  children: RenderItem(
                    `Policy starting ${policyStartDate}`,
                    "",
                    false
                  )
                }
              ),
              showPrimaryCoverDivider(idx) && /* @__PURE__ */ jsx7(Divider3, {})
            ] })
          ] }),
          showNonPrimaryCoverDivider(cover) && /* @__PURE__ */ jsx7(Divider3, {}),
          product[cover].displayLimits && product[cover].displayLimits.map(
            (limit) => {
              return limit.label ? /* @__PURE__ */ jsx7(
                Flex3,
                {
                  className: "mobius-payment-summary__flex-margin-bottom",
                  children: RenderItem(limit.label, limit.value, false)
                },
                limit.label
              ) : null;
            }
          ),
          product[cover].displayExcessTitle && product[cover].displayExcessAmount && /* @__PURE__ */ jsx7(Flex3, { className: "mobius-payment-summary__flex-margin-bottom", children: RenderItem(
            product[cover].displayExcessTitle,
            formatAmount(product[cover].displayExcessAmount),
            false
          ) }),
          product[cover].displaySpecificExcesses && /* @__PURE__ */ jsxs8(Fragment6, { children: [
            Object.keys(product[cover].displaySpecificExcesses).map(
              (excess) => /* @__PURE__ */ jsx7(
                Flex3,
                {
                  className: "mobius-payment-summary__flex-margin-bottom",
                  children: /* @__PURE__ */ jsx7(Fragment6, { children: RenderItem(
                    excess,
                    formatAmount(
                      product[cover].displaySpecificExcesses[excess]
                    ),
                    false
                  ) })
                },
                excess
              )
            ),
            showSecondaryCoverDivider(idx) && /* @__PURE__ */ jsx7(Divider3, {})
          ] })
        ] }, cover)),
        (minMobileCovers || minDesktopCovers) && /* @__PURE__ */ jsx7(
          Flex3,
          {
            className: `mobius-payment-summary__minimum-covers ${applicationDashboard && !productCovers.some((r) => covers.indexOf(r) >= 0) && !isMobile ? "mobius-payment-summary__minimum-covers-desktop" : ""}`
          }
        )
      ]
    }
  );
}
function ProductSummary({
  productName,
  product,
  additionalCover,
  totalGrossPremium,
  mobileView,
  policyStartDate,
  hasSharedLimits,
  applicationDashboard = false,
  updateApplicationDashboard,
  isOnlyProduct,
  hasPrimaryCover
}) {
  const [loading, setIsLoading] = useState4(false);
  const additionalCoversKeys = Object.keys(additionalCover);
  const oneOrMoreProducts = Object.keys(product).length > 0;
  const [eventDetails, setEventDetails] = useState4({ eventName: null, args: [] });
  useTriggerEventTracker(eventDetails.eventName, ...eventDetails.args);
  const removeProduct = async () => {
    const url = `?step_action=remove_product`;
    const payload = JSON.stringify({ product_name: productName });
    const response = await fetchRequest(url, payload);
    if (response) {
      updateApplicationDashboard(response);
      setIsLoading(false);
    }
  };
  const handleRemoveButtonClick = (ev) => {
    ev.preventDefault();
    setIsLoading(true);
    setEventDetails({
      eventName: "dashboard_remove_product",
      args: [productName ? productName : ""]
    });
    void removeProduct();
  };
  const removeButton = () => {
    return isOnlyProduct ? "" : /* @__PURE__ */ jsx7(
      Button4,
      {
        size: "sm",
        variant: "ghost",
        isLoading: loading,
        isDisabled: loading,
        onClick: (ev) => {
          handleRemoveButtonClick(ev);
        },
        children: /* @__PURE__ */ jsxs8("div", { children: [
          /* @__PURE__ */ jsx7(Icon2, { icon: bin, title: "Remove product" }),
          "\xA0",
          "Remove"
        ] })
      }
    );
  };
  function checkPrimaryCover(hasPrimaryCover2, product2) {
    if (hasPrimaryCover2) {
      return {
        "Business Owners Policy": {
          displayPremium: "0",
          displaySpecificExcesses: {},
          covered: true,
          optional: false,
          displayLimits: [{ label: "", value: "" }],
          displayExcessTitle: "",
          displayExcessAmount: ""
        },
        ...product2
      };
    }
    return product2;
  }
  return /* @__PURE__ */ jsxs8(Fragment6, { children: [
    oneOrMoreProducts && SummaryFragment(
      checkPrimaryCover(hasPrimaryCover ? hasPrimaryCover : false, product),
      mobileView,
      policyStartDate,
      totalGrossPremium,
      hasSharedLimits,
      applicationDashboard,
      hasPrimaryCover
    ),
    additionalCoversKeys.length > 0 && additionalCoversKeys.map((newCover) => {
      const newCoverRecord = {
        [newCover]: additionalCover[newCover]
      };
      return /* @__PURE__ */ jsxs8(Fragment5, { children: [
        oneOrMoreProducts && /* @__PURE__ */ jsx7(
          Text6,
          {
            className: "mobius-payment-summary__additional-covers",
            elementType: "p",
            children: " "
          }
        ),
        SummaryFragment(
          newCoverRecord,
          mobileView,
          oneOrMoreProducts ? "" : policyStartDate,
          oneOrMoreProducts ? "" : totalGrossPremium,
          hasSharedLimits,
          applicationDashboard,
          hasPrimaryCover
        )
      ] }, newCover);
    }),
    applicationDashboard && /* @__PURE__ */ jsxs8("div", { className: "mobius-payment-summary__button-div", children: [
      /* @__PURE__ */ jsxs8(
        Flex3,
        {
          justifyContent: "space-between",
          className: "mobius-payment-summary__flex-margin-top",
          children: [
            /* @__PURE__ */ jsx7(
              Icon2,
              {
                icon: circleTick,
                color: "var(--color-primary)",
                title: "Quote completed successfully"
              }
            ),
            /* @__PURE__ */ jsx7("span", { className: "mobius-payment-summary__big-button-span", children: "Quote completed" })
          ]
        }
      ),
      removeButton()
    ] })
  ] });
}

// src/components/shared/PersistentSummary/SummaryWrapper.tsx
import { plus, pencil, bin as bin2 } from "@simplybusiness/icons";
import { Image, Segment as Segment5, Button as Button5, Flex as Flex4, Icon as Icon3 } from "@simplybusiness/mobius";
import { useBreakpoint as useBreakpoint2 } from "@simplybusiness/mobius";
import { Fragment as Fragment7, jsx as jsx8, jsxs as jsxs9 } from "react/jsx-runtime";
function SummaryWrapper({
  productName,
  productSummary,
  totalGrossPremium,
  hasSharedLimits,
  insurerLogo,
  policyStartDate,
  expandCover,
  mobileView,
  editURL,
  markedForPurchase,
  onUpdateItem,
  cartItemReference,
  promptInformation = { promptCustomer: false },
  eligibleForRemoval,
  editModalDescription,
  applicationDashboard = false,
  updateApplicationDashboard,
  isOnlyProduct,
  hasPrimaryCover
}) {
  let additionalCover = {};
  const [editQuote, setIsEditQuote] = useState5(false);
  const [removeItem, setIsRemovedItem] = useState5(false);
  const [addItem, setIsAddedItem] = useState5(false);
  const isCombinedPaymentPage = useContext6(CombinedPaymentContext);
  const [eventName, setEventName] = useState5(null);
  useTriggerEventTracker(eventName);
  const productSummaryKeys = Object.keys(productSummary);
  const product = productSummaryKeys.reduce((products, key) => {
    if (!productSummary[key].optional) {
      products[key] = productSummary[key];
    } else {
      additionalCover[key] = productSummary[key];
    }
    return products;
  }, {});
  const breakpointSize = useBreakpoint2([
    { size: "sm", value: 567 },
    { size: "md", value: 568 },
    { size: "lg", value: 812 }
  ]).breakpoint.size;
  const isMobileLandScape = breakpointSize == "md";
  const setButtonStyle = () => {
    return {
      alignSelf: "center",
      padding: isMobileLandScape && "6px",
      fontSize: isMobileLandScape && "var(--font-size-2)"
    };
  };
  const editButton = /* @__PURE__ */ jsx8(
    Button5,
    {
      className: "mobius-payment-summary__button",
      variant: "primary",
      size: "sm",
      isDisabled: editQuote,
      onPress: () => {
        try {
          setEventName("go_back_to_quote_selection_clicked");
        } catch (e) {
          console.error("Error in TriggerEventTracker", e);
        }
      },
      children: isCombinedPaymentPage ? /* @__PURE__ */ jsx8(Fragment7, { children: "Edit" }) : /* @__PURE__ */ jsxs9("div", { children: [
        /* @__PURE__ */ jsx8(Icon3, { icon: pencil, title: "Edit product details" }),
        "\xA0",
        "Edit"
      ] })
    }
  );
  const toggleItem = async (cartItemReference2) => {
    const url = `?step_action=update_cart_item`;
    const payload = JSON.stringify({
      item_id: cartItemReference2,
      action: "update"
    });
    const response = await fetchRequest(url, payload);
    onUpdateItem(response, cartItemReference2);
    if (response) {
      setIsAddedItem(false);
      setIsRemovedItem(false);
    }
  };
  return /* @__PURE__ */ jsx8(Fragment7, { children: /* @__PURE__ */ jsxs9(Segment5, { children: [
    /* @__PURE__ */ jsxs9(Flex4, { className: "mobius-payment-summary__summary-wrapper-segment", children: [
      /* @__PURE__ */ jsx8(Image, { className: "mobius-payment-summary__styled-image", src: insurerLogo, alt: "insurerLogo" }),
      !eligibleForRemoval && !promptInformation.promptCustomer && editURL && /* @__PURE__ */ jsx8(
        DialogueModal2,
        {
          cancelButtonText: "Cancel, return to checkout",
          headerText: "Editing your coverage",
          actionButtonText: "Go back to your quote selection",
          redirectFunction: () => window.location.href = editURL,
          description: editModalDescription || ``,
          button: editButton
        }
      ),
      eligibleForRemoval && !promptInformation.promptCustomer ? markedForPurchase ? /* @__PURE__ */ jsx8(
        Button5,
        {
          className: "mobius-payment-summary__button",
          variant: "primary",
          size: "sm",
          isLoading: removeItem,
          isDisabled: removeItem,
          onPress: () => {
            setIsRemovedItem(true);
            toggleItem(cartItemReference);
          },
          children: /* @__PURE__ */ jsxs9("div", { children: [
            /* @__PURE__ */ jsx8(Icon3, { icon: bin2, title: "Remove product from cart" }),
            "\xA0",
            "Remove"
          ] })
        }
      ) : /* @__PURE__ */ jsx8(
        Button5,
        {
          className: "mobius-payment-summary__button",
          variant: "primary",
          size: "sm",
          isLoading: addItem,
          isDisabled: addItem,
          onPress: () => {
            setIsAddedItem(true);
            toggleItem(cartItemReference);
          },
          children: /* @__PURE__ */ jsxs9("div", { children: [
            /* @__PURE__ */ jsx8(Icon3, { icon: plus, title: "Add product to cart" }),
            "\xA0",
            "Add"
          ] })
        }
      ) : ""
    ] }),
    /* @__PURE__ */ jsx8(
      "div",
      {
        className: `mobius-payment-summary__summary-wrapper-div persistent_product_summary ${!markedForPurchase && "exclude-for-purchase"}`,
        children: /* @__PURE__ */ jsx8(
          ProductSummary,
          {
            productName,
            product,
            additionalCover,
            totalGrossPremium,
            hasSharedLimits,
            mobileView,
            policyStartDate,
            applicationDashboard,
            updateApplicationDashboard,
            isOnlyProduct,
            hasPrimaryCover
          }
        )
      }
    )
  ] }) });
}

// src/components/shared/PersistentSummary/PersistentCartPaymentOptions/CallToAction.tsx
import { Button as Button6, Flex as Flex5, Image as Image2, Text as Text7, Icon as Icon4 } from "@simplybusiness/mobius";
import { lock } from "@simplybusiness/icons";
import { jsx as jsx9, jsxs as jsxs10 } from "react/jsx-runtime";
var CallToAction = ({
  enableCheckoutFormSubmission,
  isFormSubmitting,
  fireCheckoutButtonClickedEvent,
  onCallToActionClick,
  buttonText,
  poweredByStripeLogo
}) => {
  const extraClassName = enableCheckoutFormSubmission ? "" : "combined-payment-page-sticky";
  return /* @__PURE__ */ jsx9(
    "div",
    {
      className: `mobius-payment-summary__form-actions-box ${extraClassName}`,
      children: /* @__PURE__ */ jsx9(Flex5, { className: "mobius-payment-cta-container", children: enableCheckoutFormSubmission ? /* @__PURE__ */ jsx9(
        Button6,
        {
          className: "mobius-payment-summary__checkout-button",
          type: "submit",
          size: "md",
          isLoading: isFormSubmitting,
          isDisabled: isFormSubmitting,
          "data-testid": "submit-btn",
          onClick: fireCheckoutButtonClickedEvent,
          children: "Checkout"
        }
      ) : /* @__PURE__ */ jsxs10(Flex5, { flexDirection: "column", children: [
        /* @__PURE__ */ jsx9(
          Button6,
          {
            className: "mobius-payment-summary__checkout-button combined-payment-page-sticky",
            size: "md",
            "data-testid": "open-payment-drawer-btn",
            onClick: () => onCallToActionClick(true),
            children: buttonText
          }
        ),
        /* @__PURE__ */ jsxs10(
          Flex5,
          {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            children: [
              /* @__PURE__ */ jsx9(
                Icon4,
                {
                  icon: lock,
                  className: "mobius-call-to-action_secure-checkout-icon"
                }
              ),
              /* @__PURE__ */ jsxs10(Text7, { className: "mobius-call-to-action_secure-checkout-text", children: [
                "Secure Checkout",
                " "
              ] }),
              /* @__PURE__ */ jsx9(
                Image2,
                {
                  className: "powered-by-stripe-logo",
                  src: poweredByStripeLogo,
                  alt: "Powered by Stripe"
                }
              )
            ]
          }
        )
      ] }) })
    }
  );
};

// src/components/shared/PersistentSummary/PersistentCartPaymentOptions/PersistentCartPaymentOptions.tsx
import { Fragment as Fragment8, jsx as jsx10, jsxs as jsxs11 } from "react/jsx-runtime";
var PersistentCartPaymentOptions = ({
  businessInformation,
  productsInformation,
  paymentBreakdown,
  currency,
  totalPaymentAmount,
  requestPath,
  csrfToken,
  journeyToken,
  continuationProduct = "",
  multiProductText,
  onPurchaseCartDigestUpdate = (_newValue) => {
  },
  onPaymentOptionsUpdate = (_productInformation) => {
  },
  onCallToActionClick = (_newValue) => {
  },
  onPayTodayAmountUpdate = (_payTodayAmount) => {
  },
  showPaymentSummaryOnly = false,
  enableCheckoutFormSubmission = true,
  enablePaymentOptionsServerUpdate = false,
  combinedPaymentPage = false,
  isExternalCarrier = false,
  hasMonthlyFees = false,
  cardFeeDisclosureText = "",
  poweredByStripeLogo = ""
}) => {
  const [productsInfo, setProductsInfo] = useState6(productsInformation);
  const [dynamicPaymentBreakdown, setDynamicPaymentBreakdown] = useState6(paymentBreakdown);
  const [totalPayment, setTotalPayment] = useState6(totalPaymentAmount);
  const [isFormSubmitting, setIsFormSubmitting] = useState6(false);
  const productsMarkedForPurchase = selectProductsMarkedForPurchase(productsInfo);
  const breakpointSize = useBreakpoint3([
    { size: "sm", value: 639 },
    { size: "lg", value: 640 }
  ]).breakpoint.size;
  const [eventDetails, setEventDetails] = useState6({ eventName: null, args: [] });
  useTriggerEventTracker(eventDetails.eventName, ...eventDetails.args);
  const firePaymentOptionChangeEvent = (cartItemReference, newPaymentPeriod) => {
    const currentProduct = productsInfo.find(
      (product) => product.cartItemReference === cartItemReference
    );
    try {
      setEventDetails({
        eventName: "payment_option_changed",
        args: [
          newPaymentPeriod,
          ...currentProduct ? currentProduct.coverNames : [""]
        ]
      });
    } catch (e) {
      console.error("Error in TriggerEventTracker", e);
    }
  };
  const fireCheckoutButtonClickedEvent = () => {
    productsInfo.forEach((product) => {
      setEventDetails({
        eventName: "checkout_button_clicked",
        args: [
          product.selectedPaymentOption,
          ...product ? product.coverNames : [""]
        ]
      });
    });
  };
  const handleUpdateItem = (response) => {
    setProductsInfo(response.productsInformation);
    setTotalPayment(response.totalPaymentAmount);
    setDynamicPaymentBreakdown(response.paymentBreakdown);
    onPaymentOptionsUpdate(response.productsInformation);
    onPurchaseCartDigestUpdate(response.purchase_cart_digest);
  };
  const handlePaymentOptionsClientSideUpdate = (cartItemReference, newPaymentPeriod) => {
    firePaymentOptionChangeEvent(cartItemReference, newPaymentPeriod);
    setProductsInfo(
      (prevState) => prevState.map(
        (productInfo) => productInfo.cartItemReference === cartItemReference ? { ...productInfo, selectedPaymentOption: newPaymentPeriod } : productInfo
      )
    );
  };
  const upfrontPaymentsTotalValue = useMemo(() => {
    return upfrontPaymentsTotal(productsMarkedForPurchase, currency);
  }, [productsMarkedForPurchase, currency]);
  const buttonText = `Pay ${upfrontPaymentsTotalValue || ""}`;
  useEffect3(() => {
    onPayTodayAmountUpdate(buttonText);
  }, [buttonText, onPayTodayAmountUpdate]);
  const upfrontPaymentBreakdown = () => {
    if (!enablePaymentOptionsServerUpdate) {
      return upfrontPayments(productsMarkedForPurchase, currency);
    }
    return dynamicPaymentBreakdown.downpayment;
  };
  const monthlyPaymentBreakdown = () => {
    if (!enablePaymentOptionsServerUpdate) {
      return monthlyPayments(
        productsMarkedForPurchase,
        currency,
        hasMonthlyFees
      );
    }
    return dynamicPaymentBreakdown.installments;
  };
  const buildFormAttributes = () => {
    if (!enableCheckoutFormSubmission) return {};
    return {
      action: requestPath,
      method: "POST",
      onSubmit: () => setIsFormSubmitting(true)
    };
  };
  return /* @__PURE__ */ jsx10(CombinedPaymentContext.Provider, { value: combinedPaymentPage, children: /* @__PURE__ */ jsxs11(
    "form",
    {
      "data-testid": "checkout-form",
      id: "checkout-form",
      ...buildFormAttributes(),
      children: [
        /* @__PURE__ */ jsx10(
          "input",
          {
            className: "hidden",
            name: "authenticity_token",
            type: "hidden",
            "data-testid": "authenticity-token",
            value: csrfToken
          }
        ),
        /* @__PURE__ */ jsx10(
          "input",
          {
            className: "hidden",
            name: "journey_token",
            type: "hidden",
            value: journeyToken,
            "data-testid": "journey-token"
          }
        ),
        /* @__PURE__ */ jsxs11(
          "div",
          {
            className: `mobius-payment-summary__outer-grid ${combinedPaymentPage ? "combined-payment-page" : void 0} ${showPaymentSummaryOnly ? "show-payment-summary-only" : void 0}`,
            children: [
              !showPaymentSummaryOnly && /* @__PURE__ */ jsxs11(Fragment8, { children: [
                /* @__PURE__ */ jsx10(
                  "div",
                  {
                    className: `mobius-payment-summary__product-payment-options ${combinedPaymentPage ? "combined-styles" : void 0}`,
                    children: productsInfo.map((productInfo) => /* @__PURE__ */ jsxs11(
                      SegmentGroup2,
                      {
                        className: !productInfo.promptInformation?.promptCustomer ? "product-group" : "product-group non-purchasable-product",
                        "data-testid": productInfo.cartItemReference,
                        horizontal: breakpointSize == "lg",
                        children: [
                          /* @__PURE__ */ jsx10(
                            SummaryWrapper,
                            {
                              productName: "",
                              updateApplicationDashboard: (response) => {
                                console.log("updateApplicationDashboard");
                                console.log(response);
                              },
                              productSummary: productInfo.productSummary.coverInformation,
                              policyStartDate: productInfo.productSummary.policyStartDate,
                              insurerLogo: productInfo.productSummary.insurerLogo,
                              hasPrimaryCover: productInfo.productSummary.hasPrimaryCover,
                              hasSharedLimits: productInfo.productSummary.hasSharedLimits,
                              mobileView: true,
                              expandCover: false,
                              editURL: productInfo.editUrl,
                              markedForPurchase: productInfo.markedForPurchase,
                              onUpdateItem: handleUpdateItem,
                              cartItemReference: productInfo.cartItemReference,
                              promptInformation: productInfo.promptInformation,
                              eligibleForRemoval: productInfo.eligibleForRemoval,
                              editModalDescription: productInfo.editModalDescription
                            }
                          ),
                          /* @__PURE__ */ jsx10(
                            PaymentOptions,
                            {
                              annual: productInfo.availablePaymentOptions.annual,
                              monthly: productInfo.availablePaymentOptions.monthly,
                              currency,
                              selectedOption: productInfo.selectedPaymentOption,
                              cartItemReference: productInfo.cartItemReference,
                              fireEvent: firePaymentOptionChangeEvent,
                              markedForPurchase: productInfo.markedForPurchase,
                              promptInformation: productInfo.promptInformation,
                              onUpdateItem: handleUpdateItem,
                              enablePaymentOptionsServerUpdate,
                              hasMonthlyFees,
                              onPaymentOptionsClientUpdate: handlePaymentOptionsClientSideUpdate
                            }
                          )
                        ]
                      },
                      productInfo.cartItemReference
                    ))
                  }
                ),
                continuationProduct.length > 0 && /* @__PURE__ */ jsx10(
                  JourneyContinuationNotice,
                  {
                    continuationProduct
                  }
                ),
                /* @__PURE__ */ jsx10(
                  CallToAction,
                  {
                    enableCheckoutFormSubmission,
                    isFormSubmitting,
                    fireCheckoutButtonClickedEvent,
                    onCallToActionClick,
                    buttonText,
                    poweredByStripeLogo
                  }
                )
              ] }),
              /* @__PURE__ */ jsx10("div", { className: "mobius-payment-summary__payment-summary-box", children: /* @__PURE__ */ jsx10("div", { className: "payment-summary", children: /* @__PURE__ */ jsx10(
                PaymentSummary,
                {
                  primaryTrade: businessInformation.primaryTrade,
                  businessName: businessInformation.businessName,
                  upfrontPayments: upfrontPaymentBreakdown(),
                  upfrontPaymentsTotal: upfrontPaymentsTotalValue,
                  monthlyPayments: monthlyPaymentBreakdown(),
                  monthlyPaymentSummary: monthlyPaymentSummary(
                    productsMarkedForPurchase,
                    currency
                  ),
                  firstMonthlyPaymentDate: firstMonthlyPaymentDate(
                    productsMarkedForPurchase,
                    currency
                  ),
                  totalPaymentAmount: currencyFormatter(currency).format(
                    totalPayment
                  ),
                  nonRefundableTexts: productInfoTexts(
                    productsMarkedForPurchase,
                    "nonRefundableText"
                  ),
                  surchargeTexts: productInfoTexts(
                    productsMarkedForPurchase,
                    "surchargeText"
                  ),
                  multiProductText: multiProductText ? multiProductText : "",
                  cardFeeDisclosureText,
                  isExternalCarrier,
                  hasMonthlyFees
                }
              ) }) })
            ]
          }
        )
      ]
    }
  ) });
};

// src/components/shared/PersistentSummary/PriceSummary/index.tsx
import { Box, Flex as Flex6, Text as Text8 } from "@simplybusiness/mobius";
import { jsx as jsx11, jsxs as jsxs12 } from "react/jsx-runtime";
function PriceSummary({ priceModel }) {
  const { downpaymentAmount, annualPaymentAmount, installmentCounts } = priceModel;
  return /* @__PURE__ */ jsxs12(Box, { className: "mobius-payment-summary__price-summary-box", children: [
    downpaymentAmount && /* @__PURE__ */ jsxs12("div", { className: "mobius-payment-summary__styled-grid mobius-payment-summary__price-summary-group mobius-payment-summary__border-bottom", children: [
      /* @__PURE__ */ jsx11(Text8, { elementType: "span", children: /* @__PURE__ */ jsx11("strong", { children: "To pay today" }) }),
      /* @__PURE__ */ jsx11(Text8, { elementType: "span", className: "align-right", children: /* @__PURE__ */ jsx11("strong", { children: downpaymentAmount }) })
    ] }),
    installmentCounts && installmentCounts.map((installmentCount, index) => /* @__PURE__ */ jsxs12(
      "div",
      {
        className: "mobius-payment-summary__styled-grid mobius-payment-summary__price-summary-group mobius-payment-summary__border-bottom",
        children: [
          /* @__PURE__ */ jsx11(Text8, { elementType: "span", children: installmentCount.labels[0] }),
          /* @__PURE__ */ jsx11(Text8, { elementType: "span", className: "align-right", children: installmentCount.value })
        ]
      },
      index
    )),
    /* @__PURE__ */ jsx11(Flex6, { children: /* @__PURE__ */ jsxs12("div", { className: "mobius-payment-summary__styled-grid mobius-payment-summary__price-summary-group", children: [
      /* @__PURE__ */ jsxs12(Text8, { elementType: "span", children: [
        "Total",
        "\xA0",
        /* @__PURE__ */ jsx11(Text8, { elementType: "span", children: "(inc.tax + fees)" })
      ] }),
      /* @__PURE__ */ jsx11(
        Text8,
        {
          elementType: "span",
          className: "align-right mobius-payment-summary__price-summary-text",
          children: annualPaymentAmount
        }
      )
    ] }) })
  ] });
}

// src/components/shared/PersistentSummary/PersistentCartPriceSummary/PersistentCartPriceSummary.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function PersistentCartPriceSummary({
  cartPriceModel
}) {
  const { productsInformation, currency, totalPaymentAmount } = cartPriceModel;
  const priceModel = {
    downpaymentAmount: upfrontPaymentsTotal(productsInformation, currency),
    installmentCounts: monthlyPayments(productsInformation, currency),
    annualPaymentAmount: currencyFormatter(currency).format(totalPaymentAmount)
  };
  return /* @__PURE__ */ jsx12("div", { className: "mobius-payment-summary__persistent-price-summary", children: /* @__PURE__ */ jsx12(PriceSummary, { priceModel }) });
}

// src/components/shared/PersistentSummary/PersistentPriceSummary/PersistentPriceSummary.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var transformPriceModel = (priceModel) => {
  return priceModel.installmentCount ? {
    downpaymentAmount: priceModel.downpaymentAmount,
    installmentCounts: [
      {
        labels: [`${priceModel.installmentCount} Monthly payments of`],
        value: priceModel.installmentAmount
      }
    ],
    annualPaymentAmount: priceModel.totalPaymentAmount
  } : {
    downpaymentAmount: priceModel.totalPaymentAmount,
    installmentCounts: [],
    annualPaymentAmount: priceModel.totalPaymentAmount
  };
};
function PersistentPriceSummary({
  priceModel
}) {
  const transformedPriceModel = transformPriceModel(priceModel);
  return /* @__PURE__ */ jsx13("div", { className: "persistent_price_summary", children: /* @__PURE__ */ jsx13(PriceSummary, { priceModel: transformedPriceModel }) });
}

// src/components/shared/PersistentSummary/PersistentSummaryHeader/PersistentSummaryHeader.tsx
import { Text as Text9, Box as Box2, Flex as Flex7 } from "@simplybusiness/mobius";
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
var PersistentSummaryHeader = ({
  text: text3 = "Your quoted coverages"
}) => {
  return /* @__PURE__ */ jsxs13(Box2, { children: [
    /* @__PURE__ */ jsx14(Flex7, { className: "coverage-widget-up-arrow" }),
    /* @__PURE__ */ jsx14(Flex7, { children: /* @__PURE__ */ jsx14(
      Text9,
      {
        className: "coverage-title mobius-payment-summary__styled-coverage-text mobius-payment-summary__styled-box",
        elementType: "span",
        children: text3
      }
    ) })
  ] });
};

// src/components/shared/PersistentSummary/PersistentProductSummary/PersistentProductSummary.tsx
import { useEffect as useEffect4, useState as useState7 } from "react";
import { jsx as jsx15 } from "react/jsx-runtime";
var PersistentProductSummary = ({
  productSummary,
  expandCover,
  mobileView,
  applicationDashboard = false,
  updateApplicationDashboard,
  isOnlyProduct
}) => {
  const [isUpsell, setIsUpsell] = useState7(false);
  useEffect4(() => {
    setIsUpsell(document.getElementsByClassName("upsell-question").length > 0);
  }, []);
  if (!Array.isArray(productSummary)) {
    return null;
  }
  return /* @__PURE__ */ jsx15("div", { className: "persistent_product_summary mobius-payment-summary__persistent-product-summary-div", children: productSummary.map((product, idx) => /* @__PURE__ */ jsx15(
    SummaryWrapper,
    {
      productName: product.productName,
      productSummary: product.coverInformation,
      policyStartDate: product.policyStartDate,
      totalGrossPremium: product.totalGrossPremium,
      hasSharedLimits: product.hasSharedLimits,
      hasPrimaryCover: product.hasPrimaryCover,
      insurerLogo: product.insurerLogo,
      expandCover: isUpsell && expandCover,
      mobileView,
      applicationDashboard,
      updateApplicationDashboard,
      isOnlyProduct,
      onUpdateItem: () => {
      }
    },
    `${idx}_${product.insurerLogo}`
  )) });
};

// src/components/shared/PreQCP/PreQCPSignposting.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
var PreQCPSignposting = ({
  quotedProduct,
  journeyCurrentPagePath,
  journeyToken,
  authenticityToken,
  secondaryProduct
}) => {
  return /* @__PURE__ */ jsxs14("div", { className: "mobius-pre-qcp__pre-qcp-card-container", children: [
    /* @__PURE__ */ jsx16("div", { className: "mobius-pre-qcp__additional-rfq-body", children: /* @__PURE__ */ jsxs14("div", { className: "mobius-pre-qcp__pre-qcp-primary", children: [
      /* @__PURE__ */ jsx16("h2", { className: "mobius-pre-qcp__quoted-product-name", children: quotedProduct.name }),
      quotedProduct.min_price && /* @__PURE__ */ jsxs14("div", { className: "mobius-pre-qcp__quoted-subtext", children: [
        "From ",
        /* @__PURE__ */ jsx16("span", { children: quotedProduct.min_price }),
        " / month"
      ] }),
      /* @__PURE__ */ jsx16("div", { className: "mobius-pre-qcp__button-container", children: /* @__PURE__ */ jsxs14("form", { action: journeyCurrentPagePath, method: "post", children: [
        /* @__PURE__ */ jsx16(
          "input",
          {
            type: "hidden",
            name: "authenticity_token",
            value: authenticityToken,
            autoComplete: "off"
          }
        ),
        /* @__PURE__ */ jsx16(
          "input",
          {
            type: "hidden",
            name: "journey_token",
            id: "journey_token",
            value: journeyToken,
            autoComplete: "off"
          }
        ),
        /* @__PURE__ */ jsx16(
          "button",
          {
            id: "btn-submit",
            type: "submit",
            className: "mobius --variant-primary --size-sm mobius-button btn-primary explanation-btn",
            children: quotedProduct.hyperlink_text
          }
        )
      ] }) })
    ] }) }),
    /* @__PURE__ */ jsxs14("div", { className: "mobius-pre-qcp__additional-rfq-body", children: [
      /* @__PURE__ */ jsx16("h2", { className: "mobius-pre-qcp__quoted-product-name", children: secondaryProduct.name }),
      secondaryProduct.min_price && /* @__PURE__ */ jsxs14("div", { className: "mobius-pre-qcp__quoted-subtext", children: [
        "From ",
        /* @__PURE__ */ jsx16("span", { children: secondaryProduct.min_price }),
        " / month"
      ] }),
      /* @__PURE__ */ jsx16("div", { className: "mobius-pre-qcp__pre-qcp-continuation-subtext", children: secondaryProduct.text })
    ] })
  ] });
};

// src/components/shared/QuoteDetailsForm/QuoteDetailsForm.tsx
import { useEffect as useEffect5, useState as useState8 } from "react";

// src/components/shared/QuoteDetailsForm/BuyButton.tsx
import { Fragment as Fragment9, jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var underscoreToHyphen = (value) => value.replace(/_/g, "-");
var objectMap = (object, mapFn) => Object.fromEntries(Object.entries(object).map(mapFn));
var dataPropsFromDataObject = (data) => objectMap(data, (entry) => [`data-${underscoreToHyphen(entry[0])}`, entry[1]]);
var BuyButton = ({
  buyButtonData = { track: "test" },
  buttonLabel = "Buy"
}) => {
  const dataAttributes = dataPropsFromDataObject(buyButtonData);
  return /* @__PURE__ */ jsxs15(Fragment9, { children: [
    /* @__PURE__ */ jsx17(
      "button",
      {
        type: "submit",
        className: "btn-primary mobius-quote-details-form__buy_button buy-button js-buy-button portable-hide qa-buy-quote-option",
        ...dataAttributes,
        children: /* @__PURE__ */ jsx17("span", { children: buttonLabel })
      }
    ),
    /* @__PURE__ */ jsx17(
      "button",
      {
        type: "submit",
        className: "btn-primary mobius-quote-details-form__buy_button buy-button js-buy-button portable-show us-buy-button",
        ...dataAttributes,
        children: /* @__PURE__ */ jsx17("span", { children: buttonLabel })
      }
    ),
    /* @__PURE__ */ jsx17(
      "button",
      {
        type: "submit",
        className: "btn-primary mobius-quote-details-form__buy_button buy-button portable-show uk-details-button",
        ...dataAttributes,
        children: /* @__PURE__ */ jsx17("span", { children: buttonLabel })
      }
    )
  ] });
};

// src/components/shared/QuoteDetailsForm/QuoteDetailsForm.tsx
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var QuoteDetailsForm = ({
  buyButtonData,
  formAuthenticityToken,
  insurer,
  quoteId,
  journeyToken,
  buttonLabel
}) => {
  const [action, setAction] = useState8("");
  useEffect5(() => {
    setAction(document.location.toString());
  }, []);
  return /* @__PURE__ */ jsxs16("form", { action, method: "post", children: [
    /* @__PURE__ */ jsx18(
      "input",
      {
        type: "hidden",
        name: "journey_token",
        "data-testid": "journey_token",
        value: journeyToken
      }
    ),
    /* @__PURE__ */ jsx18(
      "input",
      {
        type: "hidden",
        name: "authenticity_token",
        "data-testid": "authenticity_token",
        value: formAuthenticityToken
      }
    ),
    /* @__PURE__ */ jsx18(
      "input",
      {
        type: "hidden",
        name: "insurer",
        "data-testid": "insurer",
        value: insurer
      }
    ),
    /* @__PURE__ */ jsx18(
      "input",
      {
        type: "hidden",
        name: "quote_id",
        "data-testid": "quote_id",
        value: quoteId
      }
    ),
    /* @__PURE__ */ jsx18(
      "input",
      {
        type: "hidden",
        name: "buy_button_clicked",
        "data-testid": "buy_button_clicked",
        value: "yes"
      }
    ),
    /* @__PURE__ */ jsx18(BuyButton, { buyButtonData, buttonLabel })
  ] });
};

// src/components/shared/TransferConsentPageUK/TransferConsentPageUK.tsx
import { useState as useState10 } from "react";

// src/components/shared/TransferConsentPageUK/TransferConsentFormUK/TransferConsentFormUK.tsx
import { useState as useState9 } from "react";
import { Text as Text10, LoadingIndicator, Button as Button7, Flex as Flex8 } from "@simplybusiness/mobius";

// src/components/shared/TransferConsentPageUK/values.tsx
var transferState = "TRANSFER";
var errorState = "ERROR";
var successState = "SUCCESS";
var declinedState = "DECLINED";

// src/components/shared/TransferConsentPageUK/TransferConsentFormUK/TransferConsentFormUK.tsx
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var text = {
  transfer: {
    header: "No quotes showing?",
    body: [
      `We can't currently offer you a quote but our partner, 
      Gallagher, might be able to help you.`,
      `Gallagher has a team of specialists that can offer
      you insurance advice and guidance, as well as your own dedicated account handler.`,
      `As one of the world's largest insurance brokers, Gallagher supports
      thousands of businesses with tailored insurance advice to arrange either standard or
      specialist business insurance.`,
      `If you're happy for us to hand over your details to Gallagher, 
      they might be able to provide you with a quote.`
    ]
  }
};
var TransferConsentFormUK = ({
  transferUrl = "https://default.url",
  csrfToken = "defaultToken",
  transferState: transferState2 = () => console.log("default state")
}) => {
  const [loading, setLoadingState] = useState9(false);
  const handleSubmit = async (option) => {
    setLoadingState(true);
    const response = await fetch(transferUrl, {
      method: "POST",
      body: JSON.stringify({ consent: option === "yes" }),
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
        Accept: "application/json"
      }
    }).then((res) => res.json());
    const result = response.success ? successState : errorState;
    const stateParams = {
      state: option === "yes" ? result : declinedState,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      response
    };
    transferState2(stateParams);
  };
  return /* @__PURE__ */ jsxs17("div", { children: [
    /* @__PURE__ */ jsx19(Text10, { elementType: "h2", children: text.transfer.header }),
    text.transfer.body.map((message) => /* @__PURE__ */ jsx19(Text10, { children: message }, message)),
    loading ? /* @__PURE__ */ jsx19("div", { className: "mobius-transfer-consent__styled-div", children: /* @__PURE__ */ jsx19(LoadingIndicator, { size: "lg", "data-testid": "loading-indicator" }) }) : /* @__PURE__ */ jsxs17(
      Flex8,
      {
        className: "mobius-transfer-consent__styled-button-container",
        gap: "xs",
        children: [
          /* @__PURE__ */ jsx19(
            Button7,
            {
              size: "sm",
              variant: "secondary",
              onClick: () => {
                handleSubmit("yes").catch(console.error);
              },
              children: "Yes"
            }
          ),
          /* @__PURE__ */ jsx19(
            Button7,
            {
              size: "sm",
              variant: "secondary",
              onClick: () => {
                handleSubmit("no").catch(console.error);
              },
              children: "No"
            }
          )
        ]
      }
    )
  ] });
};

// src/components/shared/TransferConsentPageUK/TransferResultMessageUK/TransferResultMessageUK.tsx
import { Text as Text11 } from "@simplybusiness/mobius";

// src/components/shared/TransferConsentPageUK/PhoneNumber.tsx
import { Link } from "@simplybusiness/mobius";
import { jsx as jsx20 } from "react/jsx-runtime";
function PhoneNumber(number) {
  return /* @__PURE__ */ jsx20(Link, { href: `tel:${number}`, children: number });
}

// src/components/shared/TransferConsentPageUK/TransferReference.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
function TransferReference(transferStatus) {
  return /* @__PURE__ */ jsx21("span", { className: "mobius-transfer-reference__span", children: transferStatus.response.reference });
}

// src/components/shared/TransferConsentPageUK/Header.tsx
function header(transferStatus, text3) {
  switch (transferStatus.state) {
    case successState:
      return "Transfer successful";
    case errorState:
      return "Transfer unsuccessful";
    default:
      return text3.consentNotGiven.header;
  }
}

// src/components/shared/TransferConsentPageUK/TransferResultMessageUK/TransferResultMessageUK.tsx
import { Fragment as Fragment10, jsx as jsx22, jsxs as jsxs18 } from "react/jsx-runtime";
var text2 = {
  consentNotGiven: {
    header: "Transfer not completed",
    body: `Your details have not been passed to Gallagher, if you need any 
      further help call us on `,
    phone_number: "0330 912 0297"
  }
};
var TransferResultMessageUK = ({
  transferStatus
}) => {
  function Message() {
    const transferReferenceCopy = `Here is your unique customer reference number for Gallagher: `;
    switch (transferStatus.state) {
      case successState:
        return /* @__PURE__ */ jsxs18(Fragment10, { children: [
          /* @__PURE__ */ jsxs18(Text11, { children: [
            `Great, Gallagher will be in touch as soon as possible
            within their working hours. You can also contact them on `,
            PhoneNumber("01443 807415"),
            ` between 9am-5pm, Monday to Friday.`
          ] }),
          /* @__PURE__ */ jsxs18(Text11, { children: [
            transferReferenceCopy,
            TransferReference(transferStatus)
          ] })
        ] });
      case errorState:
        return /* @__PURE__ */ jsxs18(Fragment10, { children: [
          /* @__PURE__ */ jsxs18(Text11, { children: [
            `Unfortunately, we've been unable to pass your details over 
          at this time but we'll try our best to pass on your information to Gallagher. 
          If you'd like to speak with them, you can call on `,
            PhoneNumber("01443 807415"),
            ` between 9am-5pm, Monday to Friday.`
          ] }),
          /* @__PURE__ */ jsxs18(Text11, { children: [
            transferReferenceCopy,
            TransferReference(transferStatus)
          ] })
        ] });
      default:
        return /* @__PURE__ */ jsxs18(Text11, { children: [
          text2.consentNotGiven.body,
          PhoneNumber(text2.consentNotGiven.phone_number)
        ] });
    }
  }
  return /* @__PURE__ */ jsxs18("div", { children: [
    /* @__PURE__ */ jsx22(Text11, { elementType: "h2", children: header(transferStatus, text2) }),
    /* @__PURE__ */ jsx22(Message, {})
  ] });
};

// src/components/shared/TransferConsentPageUK/TransferConsentPageUK.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var TransferConsentPageUK = ({
  transferUrl,
  csrfToken
}) => {
  const [transferState2, setTransferState] = useState10({
    state: transferState,
    response: null
  });
  return /* @__PURE__ */ jsx23("div", { children: transferState2.state === transferState ? /* @__PURE__ */ jsx23(
    TransferConsentFormUK,
    {
      transferUrl,
      csrfToken,
      transferState: setTransferState
    }
  ) : /* @__PURE__ */ jsx23(TransferResultMessageUK, { transferStatus: transferState2 }) });
};

// src/pages/AgentReferralPage/index.tsx
import loadable from "@loadable/component";
var AgentReferralPage = loadable(() => import("./AgentReferralPage-NNHAFLLD.js"), {
  resolveComponent: (mod) => mod.AgentReferralPage
});

// src/pages/QuestionnairePage/index.tsx
import loadable2 from "@loadable/component";
var QuestionnairePage = loadable2(() => import("./QuestionnairePage-DCI5YRBI.js"), {
  resolveComponent: (mod) => mod.QuestionnairePage
});

// src/pages/QuoteComparisonPage/index.tsx
import loadable3 from "@loadable/component";
var QuoteComparisonPage = loadable3(
  () => import("./QuoteComparisonPage-33J3DOIW.js"),
  {
    resolveComponent: (mod) => mod.QuoteComparisonPage
  }
);
var USQCPPage = QuoteComparisonPage;
var UKQCPPage = QuoteComparisonPage;

// src/pages/ReferralPage/index.tsx
import loadable4 from "@loadable/component";
var ReferralPage = loadable4(() => import("./ReferralPage-ZGWGLHG2.js"), {
  resolveComponent: (mod) => mod.ReferralPage
});

// src/pages/PersonalisedCoverPage/index.tsx
import loadable5 from "@loadable/component";
var PersonalisedCoverPage = loadable5(
  () => import("./PersonalisedCoverPage-YRW6W6AL.js"),
  {
    resolveComponent: (mod) => mod.PersonalisedCoverPage
  }
);

// src/pages/ShowcasePage/index.tsx
import loadable6 from "@loadable/component";
var ShowcasePage = loadable6(() => import("./ShowcasePage-JMLUZIUB.js"), {
  resolveComponent: (mod) => mod.ShowcasePage
});
export {
  Actions,
  AgentActions,
  AgentInformation,
  AgentReferral,
  AgentReferralPage,
  AgentResources,
  CoverToggle,
  CoverTogglesContainer,
  CoverageSummary,
  DateQuestion,
  DialogueModal,
  ErrorBoundary,
  Footer,
  Header,
  Headline,
  InfoCard,
  InsuranceBenefits,
  LOGO_LINK,
  NextCoverage,
  OPENING_HOURS_LABEL,
  OPENING_HOURS_UK,
  OPENING_HOURS_US,
  Page,
  PageProviders,
  PersistentCartPaymentOptions,
  PersistentCartPriceSummary,
  PersistentPriceSummary,
  PersistentProductSummary,
  PersistentSummaryHeader,
  PersonalisedCoverPage,
  PreFooter,
  PreQCPSignposting,
  ProductSummary,
  ProgressSteps,
  ProgressStepsItem,
  Questionnaire,
  QuestionnairePage,
  QuoteCard,
  QuoteCards,
  QuoteComparisonPage,
  QuoteDetailsForm,
  QuoteHelp,
  Referral,
  ReferralPage,
  SecurityLocks,
  ShowcasePage,
  SubmitAction,
  TransferConsentPageUK,
  TrustSection,
  UKQCPPage,
  USQCPPage
};
